"use client";

import React, { useState, useEffect } from "react";
import { useAuth } from "@/src/contexts/AuthContext";

/* ── Haversine straight-line distance ────────────── */
function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLng = (lng2 - lng1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function formatKm(km: number): string {
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(1)} km`;
}

interface HeroBannerProps {
  title?: string;
  subtitle?: string;
  bannerImage?: string;
  logoUrl?: string;
  rating?: number;
  ratingCount?: number;
  city?: string;
  category?: string;
  hasSale?: boolean;
  saleLabel?: string;
  outletCoordinates?: [number, number] | null;
}

export const HeroBanner: React.FC<HeroBannerProps> = ({
  title = "GrabLocal Store",
  subtitle = "Shop local favorites on GrabLocal",
  bannerImage,
  logoUrl,
  rating,
  ratingCount,
  city,
  category,
  hasSale = true,
  saleLabel = "Festive Sale · Up to 40% off",
  outletCoordinates,
}) => {
  const { isAuthenticated } = useAuth();
  const initial = title.charAt(0).toUpperCase();
  const displayRating = rating ? rating.toFixed(1) : null;

  const [distanceLabel, setDistanceLabel] = useState<string | null>(null);
  const [deliveryLabel, setDeliveryLabel] = useState<string>("Fast delivery");

  function getDeliveryLabel(km: number): string {
    if (km < 10)  return "Instant";
    if (km < 30)  return "Sameday";
    if (km < 250) return "Express";
    return "Normal";
  }

  const calcDistance = () => {
    if (!outletCoordinates) return;
    try {
      const raw = localStorage.getItem("selected_address_coords");
      if (!raw) return;
      const coords = JSON.parse(raw) as { lat: number; lng: number } | [number, number];
      const addrLat = Array.isArray(coords) ? coords[0] : coords.lat;
      const addrLng = Array.isArray(coords) ? coords[1] : coords.lng;
      const km = haversineKm(addrLat, addrLng, outletCoordinates[0], outletCoordinates[1]);
      setDistanceLabel(formatKm(km));
      setDeliveryLabel(getDeliveryLabel(km));
    } catch { /* ignore */ }
  };

  useEffect(() => {
    calcDistance();
    window.addEventListener("address_changed", calcDistance);
    return () => window.removeEventListener("address_changed", calcDistance);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outletCoordinates]);

  return (
    <>
      {/* ── HERO SHELL ── */}
      <div className="mt-4">
        <div
          className="relative overflow-hidden"
          style={{
            borderRadius: "clamp(14px,2vw,20px)",
            height: "clamp(220px,32vw,360px)",
            background: bannerImage
              ? undefined
              : `
                radial-gradient(ellipse at 30% 40%, rgba(210,180,160,0.35), transparent 55%),
                radial-gradient(ellipse at 75% 60%, rgba(180,140,100,0.20), transparent 55%),
                linear-gradient(170deg, #D4CABC 0%, #C4B8A4 40%, #B8AA94 100%)
              `,
          }}
        >
          {bannerImage && (
            <img
              src={bannerImage}
              alt="Store banner"
              className="h-full w-full object-cover object-center"
            />
          )}

          {/* Noise texture overlay */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
              opacity: 0.06,
            }}
          />

          {/* Sale ribbon */}
          {/* {hasSale && (
            <div
              className="absolute flex items-center gap-2.5 font-bold text-white"
              style={{
                top: "clamp(20px,3vw,28px)",
                left: "clamp(20px,3vw,32px)",
                padding: "10px 16px",
                background: "var(--rising-sun)",
                borderRadius: 999,
                fontSize: "clamp(11px,1.4vw,13px)",
                letterSpacing: "0.3px",
                zIndex: 3,
                boxShadow: "0 6px 20px rgba(228,96,78,0.35)",
                animation: "ribbonPulse 2.4s ease-in-out infinite",
              }}
            >
              <span
                className="rounded-full"
                style={{
                  width: 8,
                  height: 8,
                  background: "var(--turmeric)",
                  animation: "dotPulse 1.4s ease-in-out infinite",
                  display: "inline-block",
                }}
              />
              {saleLabel}
            </div>
          )} */}

          {/* Location label bottom-left */}
          <div
            className="absolute flex items-center gap-2 font-semibold uppercase tracking-[1.5px]"
            style={{
              bottom: "clamp(20px,3vw,28px)",
              left: "clamp(20px,3vw,32px)",
              fontSize: "11.5px",
              color: "rgba(255,255,255,0.92)",
              zIndex: 2,
            }}
          >
            <span style={{ width: 24, height: 1, background: "rgba(255,255,255,0.7)", display: "inline-block" }} />
            {[category, city].filter(Boolean).join(" · ") || "GrabLocal"}
          </div>

          {/* Action buttons top-right */}
          {/* <div
            className="absolute flex gap-2"
            style={{
              top: "clamp(20px,3vw,28px)",
              right: "clamp(20px,3vw,28px)",
              zIndex: 2,
            }}
          >
            <button
              className="flex items-center justify-center rounded-xl transition"
              style={{
                width: 44,
                height: 44,
                background: "rgba(255,255,255,0.92)",
                backdropFilter: "blur(10px)",
                color: "var(--techno-black)",
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
              </svg>
            </button>
            <button
              className="flex items-center justify-center rounded-xl transition"
              style={{
                width: 44,
                height: 44,
                background: "rgba(34,26,18,0.7)",
                color: "#fff",
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <path d="M7 17l9.2-9.2M17 17V7H7" />
              </svg>
            </button>
          </div> */}

          {/* Floating store illustration */}
          {!bannerImage && (
            <div
              className="absolute left-1/2"
              style={{
                bottom: "clamp(40px,6vw,64px)",
                transform: "translateX(-50%)",
                animation: "floatY 6s ease-in-out infinite",
                width: "clamp(100px,14vw,160px)",
                height: "clamp(100px,14vw,160px)",
              }}
            >
              <div
                className="flex h-full w-full items-center justify-center rounded-[24px] font-extrabold"
                style={{
                  background: "rgba(255,255,255,0.12)",
                  border: "1px solid rgba(255,255,255,0.2)",
                  fontSize: "clamp(48px,8vw,80px)",
                  color: "rgba(255,255,255,0.4)",
                  letterSpacing: "-3px",
                }}
              >
                {initial}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── BRAND CARD (overlaps hero) ── */}
      <div
        className="relative"
        style={{
          marginTop: "clamp(-44px,-3.5vw,-56px)",
          zIndex: 5,
        }}
      >
        <div
          className="grid grid-cols-1 items-start gap-4 rounded-[clamp(14px,2vw,20px)] p-[clamp(20px,3vw,32px)] md:grid-cols-[1fr_auto] md:items-center"
          style={{
            background: "var(--surface)",
            boxShadow: "0 8px 40px rgba(60,40,20,0.07), 0 1px 0 var(--border-soft)",
          }}
        >
          {/* Brand identity */}
          <div className="flex items-center gap-[clamp(12px,2vw,18px)] min-w-0">
            {/* Logo mark */}
            <div
              className="flex shrink-0 items-center justify-center rounded-2xl"
              style={{
                width: "clamp(52px,7vw,64px)",
                height: "clamp(52px,7vw,64px)",
                background: "linear-gradient(135deg, #F0E4D4, #E8DACA)",
                boxShadow: "inset 0 2px 6px rgba(140,100,60,0.1)",
              }}
            >
              {logoUrl ? (
                <img src={logoUrl} alt={title} className="h-full w-full rounded-2xl object-cover" />
              ) : (
                <span
                  className="font-extrabold"
                  style={{ fontSize: "clamp(22px,3vw,28px)", color: "rgba(140,100,60,0.5)" }}
                >
                  {initial}
                </span>
              )}
            </div>

            <div className="min-w-0">
              {/* Name row */}
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <h1
                  className="font-bold leading-tight"
                  style={{
                    fontSize: "clamp(18px,2.5vw,24px)",
                    letterSpacing: "-0.6px",
                    color: "var(--techno-black)",
                  }}
                >
                  {title}
                </h1>
                {/* Verified tick */}
                <span
                  className="flex shrink-0 items-center justify-center rounded-full"
                  style={{
                    width: 18,
                    height: 18,
                    background: "var(--techno-black)",
                  }}
                  title="Verified by GrabLocal"
                >
                  <svg width="10" height="10" viewBox="0 0 14 14">
                    <path d="M3 7l2.5 2.5 5.5-5.5" stroke="#fff" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </div>

              <p
                className="leading-snug"
                style={{
                  fontSize: "clamp(12.5px,1.6vw,14px)",
                  color: "var(--techno-black-50)",
                  fontWeight: 500,
                }}
              >
                {subtitle}
              </p>
            </div>
          </div>

          {/* Stat pills */}
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {displayRating && (
              <div
                className="flex items-center gap-2 rounded-[10px] border px-3.5 py-2.5 text-sm font-semibold whitespace-nowrap"
                style={{
                  background: "var(--turmeric-soft)",
                  borderColor: "#FFE3A6",
                  color: "var(--techno-black)",
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="#E8A020">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01z" />
                </svg>
                <span>{displayRating}</span>
                {ratingCount && (
                  <span style={{ color: "var(--techno-black-50)", fontWeight: 500 }}>
                    ({ratingCount.toLocaleString()})
                  </span>
                )}
              </div>
            )}
            {isAuthenticated && (
              <div
                className="flex items-center gap-2 rounded-[10px] border px-3.5 py-2.5 text-sm font-semibold whitespace-nowrap"
                style={{
                  background: "var(--surface-2)",
                  borderColor: "var(--border-soft)",
                  color: "var(--techno-black)",
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.5 1.5" />
                </svg>
                <span>{deliveryLabel}</span>
              </div>
            )}
            {isAuthenticated && distanceLabel && (
              <div
                className="flex items-center gap-2 rounded-[10px] border px-3.5 py-2.5 text-sm font-semibold whitespace-nowrap"
                style={{
                  background: "rgba(228,96,78,0.07)",
                  borderColor: "rgba(228,96,78,0.2)",
                  color: "var(--techno-black)",
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="var(--rising-sun)">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 110-5 2.5 2.5 0 010 5z"/>
                </svg>
                <span>{distanceLabel}</span>
              </div>
            )}
          </div>

          {/* Trust row — spans both columns */}
          <div
            className="col-span-full grid grid-cols-2 gap-y-4 md:grid-cols-4"
            style={{
              marginTop: "clamp(16px,2.5vw,22px)",
              paddingTop: "clamp(16px,2.5vw,22px)",
              borderTop: "1px solid var(--border-soft)",
            }}
          >
            <TrustItem first color="c1" label="Curated Quality" sub="Vetted by GrabLocal" icon={
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="9" /><path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            } />
            <TrustItem color="c2" label="Free delivery" sub="on orders above ₹499" icon={
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 3h15v13H1z" /><path d="M16 8h4l3 3v5h-7" />
                <circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />
              </svg>
            } />
            <TrustItem color="c3" label="Easy returns" sub="7-day return policy" icon={
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" /><path d="M9 22V12h6v10" />
              </svg>
            } />
            <TrustItem color="c4" label="Meet the maker" sub="Independent · Local" icon={
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 4-7 8-7s8 3 8 7" />
              </svg>
            } />
          </div>
        </div>
      </div>
    </>
  );
};

const TRUST_COLORS = {
  c1: { bg: "rgba(228,96,78,0.12)", color: "var(--rising-sun)" },
  c2: { bg: "rgba(53,73,48,0.12)", color: "var(--chutney-green)" },
  c3: { bg: "rgba(255,193,59,0.2)", color: "#B8860B" },
  c4: { bg: "rgba(8,61,118,0.12)", color: "var(--indigo)" },
};

const TrustItem: React.FC<{
  color: keyof typeof TRUST_COLORS;
  label: string;
  sub: string;
  icon: React.ReactNode;
  first?: boolean;
}> = ({ color, label, sub, icon, first = false }) => {
  const { bg, color: iconColor } = TRUST_COLORS[color];
  return (
    <div
      className="relative flex min-w-0 items-center gap-3"
      style={{ padding: "0 clamp(8px,1.5vw,16px)" }}
    >
      {/* Vertical divider — skip for first item */}
      {!first && (
        <div
          className="absolute left-0 top-1/2 -translate-y-1/2"
          style={{ width: 1, height: 32, background: "var(--border-soft)" }}
        />
      )}
      {/* Icon */}
      <div
        className="flex shrink-0 items-center justify-center rounded-[10px]"
        style={{ width: 36, height: 36, background: bg, color: iconColor }}
      >
        {icon}
      </div>
      {/* Text */}
      <div>
        <div className="text-sm font-semibold leading-snug" style={{ color: "var(--techno-black)" }}>
          {label}
        </div>
        <div className="text-[11.5px] font-medium" style={{ color: "var(--techno-black-50)" }}>
          {sub}
        </div>
      </div>
    </div>
  );
};
